import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Message } from '@avslack/shared';
import { listChannelTasks, setMessageTaskDone } from '../lib/api';
import { applyTaskChange, messagePlainText } from '../data/messages';
import { useUsers } from '../data/users';
import { Icon } from './Icon';

/**
 * FELADATOK - kis doboz a csatorna fejleceben, jobb felul.
 *
 * Csak akkor latszik, ha a csatornaban van feladatnak jelolt uzenet. Lenyitva
 * a nyitott feladatok allnak, kipipalhatoan; a szovegre kattintva az uzenethez
 * ugrik. Alatta, osszecsukva, a legutobb elvegzettek - ki es mikor.
 *
 * A lista a betoltott uzenet-ablaktol fuggetlen: a honapokkal ezelotti, meg
 * nyitott feladat is itt van.
 */
export function TasksBox({ channelId }: { channelId: string }) {
  const [open, setOpen] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { nameOf } = useUsers();
  const { data } = useQuery({
    queryKey: ['tasks', channelId],
    queryFn: () => listChannelTasks(channelId),
    staleTime: 60_000,
  });

  // Kivulre kattintva vagy Esc-re bezarul.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openTasks = data?.open ?? [];
  const doneTasks = data?.done ?? [];
  if (openTasks.length === 0 && doneTasks.length === 0) return null;

  const toggle = (message: Message, done: boolean) => {
    // Azonnal latszik; a szerver valasza (es a tobbieknek a WS) megerositi.
    applyTaskChange(queryClient, channelId, message.id, {
      done,
      doneBy: null,
      doneAt: done ? new Date().toISOString() : null,
    });
    void setMessageTaskDone(message.id, done)
      .then((result) => applyTaskChange(queryClient, channelId, message.id, result.task))
      .catch(() => void queryClient.invalidateQueries({ queryKey: ['tasks', channelId] }));
  };

  const jump = (message: Message) => {
    setOpen(false);
    void navigate({ to: '/c/$channelId', params: { channelId }, search: { m: message.id } });
  };

  const row = (message: Message, done: boolean) => (
    <li key={message.id} className="flex items-start gap-2 px-3 py-1.5">
      <input
        type="checkbox"
        checked={done}
        onChange={(event) => toggle(message, event.target.checked)}
        aria-label={done ? 'Visszanyitás' : 'Elvégezve'}
        className="mt-0.5 size-4 shrink-0 accent-[var(--accent)]"
      />
      <button type="button" onClick={() => jump(message)} className="min-w-0 flex-1 text-left">
        <span
          className={['line-clamp-2 text-sm', done ? 'text-ink-faint line-through' : ''].join(' ')}
        >
          {messagePlainText(message) || '(csatolmány)'}
        </span>
        <span className="block text-2xs text-ink-faint">
          {nameOf(message.userId ?? '', 'Valaki')}
          {done && message.task?.doneBy
            ? ` · kész: ${nameOf(message.task.doneBy)}${
                message.task.doneAt
                  ? `, ${new Date(message.task.doneAt).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}`
                  : ''
              }`
            : ''}
        </span>
      </button>
    </li>
  );

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        title="A csatorna feladatai"
        className={[
          'flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-xs font-semibold',
          openTasks.length > 0
            ? 'border-accent bg-accent-soft text-accent'
            : 'border-border-soft text-ink-muted',
        ].join(' ')}
      >
        <Icon name="pipa" size="sm" />
        <span className="hidden sm:inline">Feladatok</span>
        <span>{openTasks.length}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-card border border-border-soft bg-surface-raised shadow-(--shadow-modal)">
          <p className="border-b border-border-soft px-3 py-2 text-xs font-semibold">
            Nyitott feladatok ({openTasks.length})
          </p>
          <ul className="max-h-80 overflow-y-auto py-1">
            {openTasks.length === 0 && (
              <li className="px-3 py-2 text-sm text-ink-muted">Minden feladat kész.</li>
            )}
            {openTasks.map((message) => row(message, false))}
          </ul>
          {doneTasks.length > 0 && (
            <div className="border-t border-border-soft">
              <button
                type="button"
                onClick={() => setShowDone((value) => !value)}
                className="flex w-full items-center gap-1 px-3 py-2 text-left text-xs text-ink-muted hover:bg-surface-hover"
              >
                <Icon name={showDone ? 'le' : 'jobbra'} size="sm" />
                Elvégzett ({doneTasks.length})
              </button>
              {showDone && (
                <ul className="max-h-60 overflow-y-auto pb-1">
                  {doneTasks.map((message) => row(message, true))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
